import angular from "angular";

import { AccountSettings, Ajax, CompanyAccountSettings, LegalEntityType } from "@salaxy/core";
import { ReportOptions } from "@salaxy/reports";
import { SessionService } from "./SessionService";
import { EditDialogResult, UiHelpers } from "./ui";

/**
 * Provides singleton for the full editable settings object:
 * Mainly for editing, but may also be used by other parts that require access to settings.
 * This implementation is for Company employers. There may be a specialized Household employers and/or Worker, Pro version later.
 */
export class SettingsService {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$rootScope", "SessionService", "AccountSettings", "UiHelpers", "AjaxNg1", "$timeout"];

  /**
   * The current settings object or null if the object is still being loaded.
   * The loading starts as soon as the settings service is loaded in the first controller.
   */
  public current: CompanyAccountSettings;

  private reportOptions: ReportOptions;

  private _reportOptionsLoading = false;

  constructor($rootScope: angular.IRootScopeService, private sessionService: SessionService, private accountSettings: AccountSettings, private uiHelpers: UiHelpers, private ajax: Ajax, private $timeout: angular.ITimeoutService) {
    this.refresh();

    sessionService.subscribe($rootScope, () => {
      this.refresh();
    });
  }

  /** Saves the session and does the refresh, but not a complete sessionService.checkSession() as in default save().  */
  public saveWithoutSessionCheck(): Promise<CompanyAccountSettings> {
    return this.accountSettings.save(this.current).then((result) => {
      this.current = result;
      return result;
    });
  }

  /** Refreshes the current settings from the server. */
  public refresh() {
    if (this.sessionService.isSessionChecked && this.sessionService.isAuthenticated) {
      const owner = this.sessionService.session.currentAccount.owner;
      if (this.current?.owner != owner){
        this.reportOptions = null;
        this.current = null;
        this._reportOptionsLoading = false;
      }
      return this.accountSettings.get().then((result) => {
        this.current = result;
        return this.current;
      });
    } else {
      this.reportOptions = null;
      this.current = null;
      this._reportOptionsLoading = false;
    }
  }

  /** Saves the current settings back to storage. */
  public save(): Promise<CompanyAccountSettings> {
    return this.accountSettings.save(this.current).then((result) => {
      this.current = result;
      return this.sessionService.checkSession().then(() => {
        return result;
      });
    });
  }

  /**
   * Creates a new IR certificate.
   * @param transferId Transfer ID for the IR certificate.
   * @param transferPassword One time transfer password for the IR certificate.
   * @returns Information about the created IR certificate.
   */
  public createIrCertificate(transferId: string, transferPassword: string): Promise<any> {
    return this.accountSettings.createIrCertificate(transferId, transferPassword);
  }

  /**
   * Renews an existing IR certificate.
   * @returns Information about the created IR certificate.
   */
  public renewIrCertificate(): Promise<any> {
    return this.accountSettings.renewIrCertificate();
  }

  /**
   * Gets the detials of the existing IR certificate or null if no certificate exists.
   * @returns Information about the current IR certificate.
   */
  public getIrCertificate(): Promise<any> {
    return this.accountSettings.getIrCertificate();
  }

  /** Sets the eInvoice address using a dialog */
  public selectEInvoiceAddress(address: { eInvoiceReceiver?: string, eInvoiceIntermediator?: string }): Promise<EditDialogResult<any>> {
    const item = {
      receiver: address.eInvoiceReceiver,
      intermediator: address.eInvoiceIntermediator,
    };
    const logic = {
      title: "Hae osoite Y-tunnuksella",
      officialId: this.sessionService.session.currentAccount.identity.officialId,
      isLoading: false,
      addresses: [],
      isSelected: (current, item) => {
        return current.receiver === item.receiver;
      },
      isAnySelected: (current) => {
        return logic.addresses.some((x) => logic.isSelected(current, x));
      },
      setCurrent: (current, item) => {
        current.receiver = item.receiver;
        current.intermediator = item.intermediator;
      },
      search: () => {
        logic.isLoading = true;
        const path = `/payments/einvoice/receiver?officialId=${encodeURIComponent(logic.officialId)}`;
        this.ajax.getJSON(path).then((items) => {
          logic.addresses = items;
          logic.isLoading = false;
        });
      },
    };
    logic.search();
    return this.uiHelpers.openEditDialog("salaxy-components/modals/account/EInvoiceAddressList.html", item, logic).then((result) => {

      if (result.action === "ok") {
        address.eInvoiceReceiver = result.item.receiver;
        address.eInvoiceIntermediator = result.item.intermediator;
      } else if (result.action === "reset") {
        address.eInvoiceReceiver = null;
        address.eInvoiceIntermediator = null;
      }
      return result;
    });
  }

  /**
   * Gets ReportOptions
   * @returns returns tempalte ReportOptions
   */
  public getReportOptions(): Promise<ReportOptions> {
    if (this.reportOptions) {
      return Promise.resolve(this.reportOptions);
    }

    if (!this.sessionService?.session?.currentAccount ||
      this.sessionService.session.currentAccount.avatar.entityType !== LegalEntityType.Company) {
      return Promise.resolve({});
    }
    if (!this._reportOptionsLoading) {
      this._reportOptionsLoading = true;
      let reportOptions: any = {};
      const settings = this.current;
      const reporting = settings?.reporting;
      if (reporting?.layoutTemplateId) {
        this.ajax.getJSON(`${this.ajax.serverAddress}/v03/api/settings/layout/${reporting.layoutTemplateId}`).then((template) => {
          if (template) {
            reportOptions = template;
            if (reporting.logo && reporting.layoutTemplateId.toLowerCase() === "logo-report-options@FI53POYS0025543933".toLowerCase()) {
              if (!reporting.logo.trim().startsWith("<")) {
                this.ajax.getJSON(`${this.ajax.serverAddress}/v03/api/settings/logo/${reporting.logo}`).then((logoMarkup) => {
                  if (logoMarkup != null) {
                    reportOptions.logo = logoMarkup;
                  } else {
                    reportOptions.logo = reporting.logo;
                  }
                  this.reportOptions = reportOptions;
                });
              } else {
                reportOptions.logo = reporting.logo;
                this.reportOptions = reportOptions;
              }
            } else {
              this.reportOptions = reportOptions;
            }
          } else {
            this.reportOptions = reportOptions;
          }
        });
      } else {
        this.reportOptions = reportOptions;
      }
    }

    const getOptions = (): Promise<ReportOptions> => {
      return new Promise((resolve) => {
        const check = () => {
          if (this.reportOptions) {
            return resolve(this.reportOptions);
          }
          this.$timeout(() => check(), 50);
        };

        check();
      });
    };
    return getOptions();
  }
}

